import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Users,
  BookMarked,
  AlertCircle,
  CheckCircle,
  Clock,
  Search,
  Plus,
  Home,
  Book,
  FileText,
  BarChart3,
  Settings,
  Bell,
  IndianRupee,
  Grid,
  List,
  ChevronRight,
  Download,
  Activity,
  Layers,
  Library,
  Loader,
  RefreshCw,
  X,
  Upload,
  ExternalLink,
  ShieldAlert,
  LogOut,
  User,
  Building2,
  TrendingUp
} from 'lucide-react';
import api from '../api';
import LibraryRulesModal from './components/LibraryRulesModal';
import { useTenant } from '../Tenant-Admin/Layout/TenantContext';
import logoutManager from '../logoutManager';

const Dashboard = () => {
  const navigate = useNavigate();

  // Safely extract tenant context with a robust fallback guard
  let tenantData = { institutionName: 'Truyon Educational Institution', institutionId: '' };
  try {
    const tenantCtx = useTenant();
    if (tenantCtx && tenantCtx.tenantData) {
      tenantData = tenantCtx.tenantData;
    }
  } catch (e) {
    const storedTenant = localStorage.getItem('tenantData');
    if (storedTenant) {
      try {
        tenantData = JSON.parse(storedTenant);
      } catch (err) {
        /* Ignore JSON parse error */
      }
    }
  }

  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Book Stats from /api/books/stats
  const [bookStats, setBookStats] = useState({
    totalBooks: 0,
    totalCopies: 0,
    availableCopies: 0,
    issuedCopies: 0,
    availabilityRate: 0,
    categoryStats: [],
    topAuthors: [],
    topPublishers: []
  });

  // Transaction stats
  const [recentActivities, setRecentActivities] = useState([]);
  const [todaySummary, setTodaySummary] = useState({
    issued: 0,
    returned: 0,
    overdue: 0,
    newMembers: 0,
    finesCollected: 0
  });

  const [userName, setUserName] = useState('Librarian');


  const tenantId = localStorage.getItem('tenantId') || tenantData?.institutionId;

  console.log('🔍 [Dashboard] Tenant ID:', tenantId);

  // ================= FETCH BOOK STATISTICS =================
  const fetchBookStats = useCallback(async () => {
    try {
      if (!tenantId) {
        console.warn('⚠️ [Dashboard] No tenantId available for stats');
        return;
      }

      console.log('📊 [Dashboard] Fetching book stats for tenantId:', tenantId);
      const response = await api.get('/api/books/stats', {
        params: { tenantId }
      });
      console.log('📊 [Dashboard] Book stats response:', response.data);

      if (response.data?.success) {
        setBookStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching book stats:', err);
    }
  }, [tenantId]);

  // ================= FETCH DASHBOARD DATA =================
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (!tenantId) {
        console.warn('⚠️ [Dashboard] No tenantId available for dashboard data');
        setLoading(false);
        return;
      }

      console.log('📊 [Dashboard] Fetching dashboard data for tenantId:', tenantId);

      // 1. Fetch Book Statistics (Library Stats)
      await fetchBookStats();

      // 2. Fetch Recent Transactions
      try {
        const transactionsResponse = await api.get('/api/transactions', {
          params: { tenantId, limit: 5, page: 1 }
        });
        console.log('📊 [Dashboard] Transactions response:', transactionsResponse.data);
        if (transactionsResponse.data?.success) {
          setRecentActivities(transactionsResponse.data.data);
        }
      } catch (err) {
        console.error('Error fetching transactions:', err);
      }

      // 3. Fetch Today's Summary
      try {
        const summaryResponse = await api.get('/api/library/books/summary/today', {
          params: { tenantId }
        });
        console.log('📊 [Dashboard] Summary response:', summaryResponse.data);
        if (summaryResponse.data?.success) {
          setTodaySummary(summaryResponse.data.data);
        }
      } catch (err) {
        console.error('Error fetching summary:', err);
      }

      // 4. Get User Metadata
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        console.log('👤 [Dashboard] User data from localStorage:', userData);
        if (userData.name) {
          setUserName(userData.name);
        } else if (userData.firstName || userData.lastName) {
          setUserName(`${userData.firstName || ''} ${userData.lastName || ''}`.trim());
        }
      } catch (err) {
        console.error('Error parsing user info:', err);
      }

    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load library dashboard');
    } finally {
      setLoading(false);
    }
  }, [tenantId, fetchBookStats]);

  useEffect(() => {
    console.log('🚀 [Dashboard] Component mounted, fetching data...');
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  // Logout using the centralized logoutManager
  const handleConfirmLogout = async () => {
    console.warn('🚪 [Dashboard] Executing logout via logoutManager...');
    setIsLoggingOut(true);
    setShowLogoutConfirm(false);
    
    const redirectPath = '/signin';

    const result = await logoutManager.logout({
      redirectTo: redirectPath,
      showToast: false,
      onSuccess: () => {
        console.log('✅ Dashboard logged out successfully');
      }
    });

    if (!result.success) {
      // Fallback: if logout fails, still redirect
      setTimeout(() => {
        window.location.href = redirectPath;
      }, 500);
    }

    setIsLoggingOut(false);
  };

  // Module Navigation Cards
  const navCards = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      description: 'Overview & Statistics',
      path: '/librarian/dashboard',
      color: 'from-blue-500 to-blue-600',
      bg: 'bg-blue-50/60 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800'
    },
    {
      id: 'books',
      label: 'Books Inventory',
      icon: Book,
      description: 'Manage Catalog & Copies',
      path: '/librarian/books',
      color: 'from-emerald-500 to-emerald-600',
      bg: 'bg-emerald-50/60 dark:bg-emerald-900/20',
      border: 'border-emerald-200 dark:border-emerald-800'
    },
    {
      id: 'transactions',
      label: 'Circulation',
      icon: FileText,
      description: 'Issue & Return Counter',
      path: '/librarian/transactions',
      color: 'from-purple-500 to-purple-600',
      bg: 'bg-purple-50/60 dark:bg-purple-900/20',
      border: 'border-purple-200 dark:border-purple-800'
    },
    {
      id: 'members',
      label: 'Members',
      icon: Users,
      description: 'Students & Faculty',
      path: '/librarian/members',
      color: 'from-amber-500 to-amber-600',
      bg: 'bg-amber-50/60 dark:bg-amber-900/20',
      border: 'border-amber-200 dark:border-amber-800'
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: BarChart3,
      description: 'Audit & Analytics',
      path: '/librarian/reports',
      color: 'from-rose-500 to-rose-600',
      bg: 'bg-rose-50/60 dark:bg-rose-900/20',
      border: 'border-rose-200 dark:border-rose-800'
    },
    {
      id: 'settings',
      label: 'Rule Settings',
      icon: Settings,
      description: 'Fines & Loan Limits',
      path: '/librarian/settings',
      color: 'from-slate-500 to-slate-600',
      bg: 'bg-slate-100 dark:bg-gray-700/40',
      border: 'border-slate-200 dark:border-gray-700'
    },
  ];

  // Quick Counter Actions
  const quickActions = [
    {
      icon: Plus,
      label: 'Add Book Title',
      text: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      path: '/librarian/books'
    },
    {
      icon: Upload,
      label: 'CSV Bulk Import',
      text: 'text-purple-600 dark:text-purple-400',
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      border: 'border-purple-200 dark:border-purple-800',
      path: '/librarian/books'
    },
    {
      icon: BookOpen,
      label: 'Issue Book Copy',
      text: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      border: 'border-emerald-200 dark:border-emerald-800',
      path: '/librarian/transactions'
    },
    {
      icon: CheckCircle,
      label: 'Process Return',
      text: 'text-indigo-600 dark:text-indigo-400',
      bg: 'bg-indigo-50 dark:bg-indigo-900/20',
      border: 'border-indigo-200 dark:border-indigo-800',
      path: '/librarian/transactions'
    },
  ];

  const getStatusColor = (action) => {
    switch (action) {
      case 'issued': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
      case 'returned': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300';
      case 'overdue': return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300';
      case 'lost': return 'bg-slate-200 text-slate-800 dark:bg-gray-700 dark:text-slate-200';
      default: return 'bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-slate-300';
    }
  };

  const getStatusIcon = (action) => {
    switch (action) {
      case 'issued': return <BookOpen className="h-3.5 w-3.5" />;
      case 'returned': return <CheckCircle className="h-3.5 w-3.5" />;
      case 'overdue': return <AlertCircle className="h-3.5 w-3.5" />;
      case 'lost': return <X className="h-3.5 w-3.5" />;
      default: return <Activity className="h-3.5 w-3.5" />;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffHours < 24) return `${diffHours} hrs ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-IN');
  };

  const renderDashboardContent = () => (
    <div className="space-y-6">
      
      {/* Welcome Hero Card - Without Logo */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 border border-slate-200 dark:border-gray-700 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white tracking-tight">
              Welcome back, {userName}! 👋
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
              Library Dashboard Overview
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => handleNavigate('/librarian/books')}
              className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center space-x-1.5 shadow-sm shadow-blue-500/20"
            >
              <Plus className="h-4 w-4" />
              <span>Add Book</span>
            </button>
            <button
              onClick={() => setShowRulesModal(true)}
              className="px-3.5 py-2 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center space-x-1.5 shadow-sm shadow-purple-500/20"
            >
              <Settings className="h-4 w-4" />
              <span>Rules & Limits</span>
            </button>
            <button
              onClick={() => handleNavigate('/librarian/reports')}
              className="px-3.5 py-2 border border-slate-300 dark:border-gray-600 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs sm:text-sm font-semibold transition-colors flex items-center space-x-1.5"
            >
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {quickActions.map((action, index) => (
          <button
            key={index}
            onClick={() => handleNavigate(action.path)}
            className={`flex flex-col items-center justify-center p-4 rounded-2xl ${action.bg} ${action.border} border hover:shadow-md transition-all duration-150 hover:-translate-y-0.5`}
          >
            <div className={`p-3 rounded-xl ${action.bg} ${action.text}`}>
              <action.icon className="h-5 w-5" />
            </div>
            <span className={`text-xs sm:text-sm font-bold mt-2 ${action.text}`}>{action.label}</span>
          </button>
        ))}
      </div>

      {/* KPIs Grid - Using Book Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-slate-200 dark:border-gray-700 shadow-xs">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Books</p>
              <p className="text-xl font-black text-gray-900 dark:text-white mt-0.5">{bookStats.totalBooks || 0}</p>
            </div>
            <div className="p-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 rounded-xl">
              <BookOpen className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-slate-200 dark:border-gray-700 shadow-xs">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Copies</p>
              <p className="text-xl font-black text-gray-900 dark:text-white mt-0.5">{bookStats.totalCopies || 0}</p>
            </div>
            <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 rounded-xl">
              <Layers className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-slate-200 dark:border-gray-700 shadow-xs">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Available</p>
              <p className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">{bookStats.availableCopies || 0}</p>
            </div>
            <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 rounded-xl">
              <CheckCircle className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-slate-200 dark:border-gray-700 shadow-xs">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Issued Out</p>
              <p className="text-xl font-black text-amber-600 dark:text-amber-400 mt-0.5">{bookStats.issuedCopies || 0}</p>
            </div>
            <div className="p-2.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 rounded-xl">
              <BookMarked className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-slate-200 dark:border-gray-700 shadow-xs">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Availability</p>
              <p className="text-xl font-black text-purple-600 dark:text-purple-400 mt-0.5">{bookStats.availabilityRate || 0}%</p>
            </div>
            <div className="p-2.5 bg-purple-50 dark:bg-purple-900/30 text-purple-600 rounded-xl">
              <TrendingUp className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Category & Top Authors Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-5 shadow-xs">
          <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3 flex items-center">
            <Layers className="h-4 w-4 mr-2 text-blue-600" />
            Category Distribution
          </h3>
          <div className="space-y-2">
            {bookStats.categoryStats && bookStats.categoryStats.length > 0 ? (
              bookStats.categoryStats.map((cat, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-gray-700/30 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{cat._id || 'Other'}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-slate-200 dark:bg-gray-600 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                        style={{ 
                          width: `${bookStats.totalBooks > 0 ? (cat.count / bookStats.totalBooks) * 100 : 0}%` 
                        }}
                      />
                    </div>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{cat.count}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-sm text-slate-500 dark:text-slate-400 py-4">No category data available</p>
            )}
          </div>
        </div>

        {/* Top Authors */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-5 shadow-xs">
          <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3 flex items-center">
            <Users className="h-4 w-4 mr-2 text-blue-600" />
            Top Authors
          </h3>
          <div className="space-y-2">
            {bookStats.topAuthors && bookStats.topAuthors.length > 0 ? (
              bookStats.topAuthors.slice(0, 5).map((author, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-gray-700/30 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[70%]">
                    {author._id || 'Unknown'}
                  </span>
                  <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{author.count} books</span>
                </div>
              ))
            ) : (
              <p className="text-center text-sm text-slate-500 dark:text-slate-400 py-4">No author data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Module Navigation Grid */}
      <div>
        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3 flex items-center">
          <Layers className="h-4 w-4 mr-2 text-blue-600" />
          Library Portal Modules
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {navCards.map((card) => (
            <button
              key={card.id}
              onClick={() => {
                setActiveTab(card.id);
                if (card.id !== 'dashboard') {
                  handleNavigate(card.path);
                }
              }}
              className={`p-4 rounded-2xl text-left transition-all duration-200 border relative ${
                activeTab === card.id
                  ? `${card.bg} ${card.border} shadow-xs ring-2 ring-blue-500/20`
                  : `bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 hover:shadow-md`
              }`}
            >
              <div className={`p-2.5 rounded-xl w-fit ${
                activeTab === card.id
                  ? `bg-gradient-to-br ${card.color} text-white`
                  : `bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-slate-300`
              }`}>
                <card.icon className="h-5 w-5" />
              </div>

              <h4 className="text-xs sm:text-sm font-bold mt-3 text-gray-900 dark:text-white">
                {card.label}
              </h4>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">{card.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Activity Log & Counter Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Recent Desk Transactions */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-5 shadow-xs">
          <div className="flex items-center justify-between mb-4 border-b border-slate-100 dark:border-gray-700 pb-3">
            <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center">
              <Activity className="h-4 w-4 mr-2 text-blue-600" />
              Recent Desk Activity
            </h3>

            <button
              onClick={() => handleNavigate('/librarian/transactions')}
              className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline flex items-center"
            >
              View Records <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
            </button>
          </div>

          <div className="space-y-2.5">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity) => (
                <div
                  key={activity._id || activity.id}
                  className="flex items-center justify-between p-3 bg-slate-50 dark:bg-gray-700/30 rounded-xl hover:bg-slate-100/80 transition-colors"
                >
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <span className={`p-2 rounded-full flex-shrink-0 ${getStatusColor(activity.status || activity.action)}`}>
                      {getStatusIcon(activity.status || activity.action)}
                    </span>

                    <div className="min-w-0 flex-1">
                      <p className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white truncate">
                        {activity.bookTitle || activity.book}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                        {activity.memberName || activity.user} • {formatTime(activity.createdAt || activity.time)}
                      </p>
                    </div>
                  </div>

                  <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full capitalize ${getStatusColor(activity.status || activity.action)}`}>
                    {activity.status || activity.action}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Activity className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-xs font-semibold">No recent counter activity logged today</p>
              </div>
            )}
          </div>
        </div>

        {/* Today's Counter Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-5 shadow-xs space-y-4">
          <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center border-b border-slate-100 dark:border-gray-700 pb-3">
            <Clock className="h-4 w-4 mr-2 text-blue-600" />
            Today's Counter Summary
          </h3>

          <div className="space-y-2.5">
            <div className="flex justify-between items-center p-3 bg-blue-50/60 dark:bg-blue-900/20 rounded-xl border border-blue-200/60">
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center">
                <BookOpen className="h-4 w-4 mr-2 text-blue-600" />
                Books Issued
              </span>
              <span className="text-base font-black text-blue-600 dark:text-blue-400">{todaySummary.issued || 0}</span>
            </div>

            <div className="flex justify-between items-center p-3 bg-emerald-50/60 dark:bg-emerald-900/20 rounded-xl border border-emerald-200/60">
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 text-emerald-600" />
                Books Returned
              </span>
              <span className="text-base font-black text-emerald-600 dark:text-emerald-400">{todaySummary.returned || 0}</span>
            </div>

            <div className="flex justify-between items-center p-3 bg-rose-50/60 dark:bg-rose-900/20 rounded-xl border border-rose-200/60">
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center">
                <AlertCircle className="h-4 w-4 mr-2 text-rose-600" />
                Overdue Returns
              </span>
              <span className="text-base font-black text-rose-600 dark:text-rose-400">{todaySummary.overdue || 0}</span>
            </div>

            <div className="flex justify-between items-center p-3 bg-purple-50/60 dark:bg-purple-900/20 rounded-xl border border-purple-200/60">
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center">
                <Users className="h-4 w-4 mr-2 text-purple-600" />
                New Members
              </span>
              <span className="text-base font-black text-purple-600 dark:text-purple-400">{todaySummary.newMembers || 0}</span>
            </div>

            <div className="flex justify-between items-center p-3 bg-indigo-50/60 dark:bg-indigo-900/20 rounded-xl border border-indigo-200/60">
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center">
                <IndianRupee className="h-4 w-4 mr-1 text-indigo-600" />
                Fines Collected
              </span>
              <span className="text-base font-black text-indigo-600 dark:text-indigo-400">₹{todaySummary.finesCollected || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex flex-col items-center justify-center space-y-3">
        <Loader className="h-8 w-8 text-blue-600 animate-spin" />
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Loading Library Desk...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full text-center border border-slate-200 dark:border-gray-700 shadow-lg">
          <div className="p-3 bg-rose-50 dark:bg-rose-900/30 rounded-full w-fit mx-auto mb-3">
            <ShieldAlert className="h-6 w-6 text-rose-600 dark:text-rose-400" />
          </div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Portal Error</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">{error}</p>
          <button
            onClick={() => {
              fetchDashboardData();
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-colors flex items-center justify-center space-x-1.5 mx-auto"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Retry Loading</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 text-slate-900 dark:text-slate-100 flex flex-col">
      
      {/* Header Bar - Without Logo */}
      <header className="sticky top-0 z-40 w-full bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 shadow-xs flex flex-col">
        <div className="h-0.5 w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 shrink-0" />

        <div className="w-full px-3 sm:px-6 h-12 flex items-center justify-between gap-2">
          {/* Identity Group - Without Logo */}
          <div className="flex items-center gap-2.5 min-w-0 max-w-[75%]">
            <div className="p-1.5 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-lg shadow-xs flex-shrink-0">
              <Library size={16} />
            </div>

            <div className="flex flex-col min-w-0 leading-none">
              <h1 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white truncate tracking-tight">
                Library Dashboard
              </h1>
              <span className="text-[9px] font-semibold text-blue-600 dark:text-blue-400 tracking-wider uppercase mt-0.5">
                Overview & Statistics
              </span>
            </div>
          </div>

          {/* Action Tools & Profile */}
          <div className="shrink-0 flex items-center gap-2">
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search catalog..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 pr-3 py-1.5 w-44 lg:w-56 rounded-lg border border-slate-300 dark:border-gray-600 bg-slate-50 dark:bg-gray-700 text-gray-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="hidden sm:flex items-center space-x-1 bg-slate-100 dark:bg-gray-700 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1 rounded transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 shadow-xs'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                <Grid className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1 rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-gray-600 text-blue-600 shadow-xs'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                <List className="h-3.5 w-3.5" />
              </button>
            </div>

            <button className="relative p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700">
              <Bell className="h-4 w-4" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full"></span>
            </button>

            {userName && (
              <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-gray-700/60 px-2.5 py-1 rounded-lg border border-slate-200 dark:border-gray-600">
                <User size={12} className="text-slate-400" />
                <span className="font-semibold max-w-[100px] truncate">{userName}</span>
              </div>
            )}

            <button
              onClick={() => setShowLogoutConfirm(true)}
              disabled={isLoggingOut}
              className="flex items-center justify-center gap-1.5 h-7 px-2.5 bg-rose-50 dark:bg-rose-900/20 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800/60 rounded-lg transition-colors disabled:opacity-50 text-xs font-semibold"
            >
              {isLoggingOut ? (
                <span className="animate-spin h-3 w-3 border-2 border-rose-600 border-t-transparent rounded-full" />
              ) : (
                <>
                  <span className="hidden xs:inline">Logout</span>
                  <LogOut size={12} />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Sub-strip ID metadata */}
        {tenantId && (
          <div className="border-t border-slate-100 dark:border-gray-700/60 bg-slate-50/60 dark:bg-gray-900/40 px-3 sm:px-6 py-1 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-1 min-w-0">
              <Building2 size={10} className="text-slate-400 shrink-0" />
              <p className="truncate">
                Institution ID: <span className="text-slate-800 dark:text-slate-200 font-semibold">{tenantId}</span>
              </p>
            </div>
            <div className="sm:hidden font-medium truncate">
              User: <span className="text-slate-800 dark:text-slate-200 font-semibold">{userName}</span>
            </div>
          </div>
        )}
      </header>

      {/* Main Page Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-3 sm:p-6 space-y-6">
        {renderDashboardContent()}
      </main>

      {/* Footer Branding */}
      <footer className="border-t border-slate-200 dark:border-gray-700 py-4 px-4 sm:px-6 bg-white dark:bg-gray-800 mt-auto">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 gap-2">
          <p>© 2026 Truyon ERP Systems. All rights reserved.</p>
          <div className="flex items-center space-x-3">
            <a
              href="https://truyon.com"
              target="_blank"
              rel="noreferrer"
              className="hover:text-blue-600 dark:hover:text-blue-400 font-semibold flex items-center gap-1 transition-colors"
            >
              <span>truyon.com</span>
              <ExternalLink className="h-3 w-3" />
            </a>
            <span>•</span>
            <span>v2.4.0</span>
          </div>
        </div>
      </footer>

      {/* Rules Config Modal */}
      <LibraryRulesModal
        isOpen={showRulesModal}
        onClose={() => setShowRulesModal(false)}
        tenantId={tenantId}
        onSave={() => {
          fetchDashboardData();
        }}
      />

      {/* Logout Overlay Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-xs w-full shadow-2xl border border-slate-200 dark:border-gray-700 p-5 text-center">
            <div className="w-10 h-10 bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-full flex items-center justify-center mx-auto mb-3">
              <LogOut size={18} />
            </div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">
              Confirm Logout
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-xs mb-4">
              Are you sure you want to exit your library desk session?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-2 text-xs font-semibold bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmLogout}
                disabled={isLoggingOut}
                className="flex-1 py-2 text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded-xl transition-colors disabled:opacity-50"
              >
                {isLoggingOut ? 'Exiting...' : 'Logout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;